'use client';

import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ReviewApi } from '@/lib/Apis/apis/review-api';
import { getApiConfiguration } from '@/lib/api-adapter';
import { Review, CreateReviewDto } from '@/lib/Apis/models';
import { useAuth } from '@/providers/AuthProvider';

export function useProductReviews(productId: string) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['reviews', 'product', productId],
    queryFn: async () => {
      const response = await new ReviewApi(getApiConfiguration()).getReviewsByProduct(productId);
      return response.data || [];
    },
    staleTime: 3 * 60 * 1000, // 3 minutes
    enabled: !!productId,
  });

  return {
    reviews: data || [],
    isLoading,
    error: error as Error | null,
    refetch,
  };
}

export function useUserReviews(userId?: string) {
  const { user } = useAuth();
  const resolvedUserId = userId || (user as any)?._id || (user as any)?.id;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['reviews', 'user', resolvedUserId],
    queryFn: async () => {
      const response = await new ReviewApi(getApiConfiguration()).getReviewsByUser(resolvedUserId!);
      return response.data || [];
    },
    staleTime: 3 * 60 * 1000, // 3 minutes
    enabled: !!resolvedUserId,
  });

  return {
    reviews: data || [],
    isLoading,
    error: error as Error | null,
    refetch,
  };
}

export function useReviewsByRating(rating: number) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['reviews', 'rating', rating],
    queryFn: async () => {
      const response = await new ReviewApi(getApiConfiguration()).getReviewsByRating(rating);
      return response.data || [];
    },
    staleTime: 3 * 60 * 1000, // 3 minutes
    enabled: rating >= 1 && rating <= 5,
  });

  return {
    reviews: data || [],
    isLoading,
    error: error as Error | null,
    refetch,
  };
}

export function useCreateReview() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);
  const queryClient = useQueryClient();

  const createReview = useCallback(async (orderId: string, reviewData: CreateReviewDto) => {
    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await new ReviewApi(getApiConfiguration()).createReview(reviewData, orderId);
      setSuccess(true);
      // Invalidate reviews cache so new review shows up
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [queryClient]);

  const reset = useCallback(() => {
    setError(null);
    setSuccess(false);
    setIsLoading(false);
  }, []);

  return {
    createReview,
    isLoading,
    error,
    success,
    reset,
  };
}

export function useUpdateReview() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const queryClient = useQueryClient();

  const updateReview = useCallback(async (reviewId: string, reviewData: any) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await new ReviewApi(getApiConfiguration()).updateReview(reviewData, reviewId);
      // Invalidate reviews cache
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [queryClient]);

  return {
    updateReview,
    isLoading,
    error,
  };
}

export function useDeleteReview() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const queryClient = useQueryClient();

  const deleteReview = useCallback(async (reviewId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      await new ReviewApi(getApiConfiguration()).removeReview(reviewId);
      // Invalidate reviews cache
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [queryClient]);

  return {
    deleteReview,
    isLoading,
    error,
  };
}

export function useReviewStats() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['reviewStats'],
    queryFn: async () => {
      const response = await new ReviewApi(getApiConfiguration()).getReviewCount();
      return response.data || 0;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    count: data || 0,
    isLoading,
    error: error as Error | null,
    refetch,
  };
}
